import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { BehaviorSubject, Observable } from 'rxjs';
import { shareReplay, finalize, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ApigeeAuthService {
  // New JWT exchange endpoints (no Basic auth)
  private tokenUrlDev = 'https://api.dev.cardinalhealth.com/oauth2/v3/token/jwtforoktab2e';
  private tokenUrlDevB2B = 'https://api.dev.cardinalhealth.com/oauth2/v3/token/jwtforoktab2b';


  private tokenUrlStg = 'https://api.stage.cardinalhealth.com/oauth2/v3/token/jwtforoktab2e';
  private tokenUrlStgB2B = 'https://api.stage.cardinalhealth.com/oauth2/v3/token/jwtforoktab2b';

  private tokenUrlProd = 'https://api.cardinalhealth.com/oauth2/v3/token/jwtforoktab2e';
  private tokenUrlProdB2B = 'https://api.cardinalhealth.com/oauth2/v3/token/jwtforoktab2b';

  private hostname = window.location.hostname;
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private inflightRequest: Observable<any> | null = null;

  constructor(private http: HttpClient, @Inject(OKTA_AUTH) private oktaAuth: OktaAuth) {

    const existingToken = localStorage.getItem('apigee_token');
    this.tokenSubject.next(existingToken);

  }
  public getHostname(): string {
    return window.location.hostname;
  }

  getAccessToken(): Observable<any> {
    let tokenUrl: string;
    let useB2B = false;
    // determine external user (b2b) by stored userType if available
    try {
      useB2B = localStorage.getItem('userType') === 'external';
    } catch (e) {
      useB2B = false;
    }

    // host-based environment selection
    if (
      this.hostname.includes('localhost') ||
      this.hostname.includes('epcis-control-center-dev') ||
      this.hostname.includes('fileguard.dev.cardinalhealth.com')
    ) {
      tokenUrl = useB2B ? this.tokenUrlDevB2B : this.tokenUrlDev;
    } else if (
      this.hostname.includes('epcis-control-center-stg') ||
      this.hostname.includes('fileguard.stg.cardinalhealth.com')
    ) {
      tokenUrl = useB2B ? this.tokenUrlStgB2B : this.tokenUrlStg;
    } else if (
      this.hostname.includes('epcis-control-center.apps.cf.gc.cahcloud.net') ||
      this.hostname.includes('fileguard.cardinalhealth.com')
    ) {
      tokenUrl = useB2B ? this.tokenUrlProdB2B : this.tokenUrlProd;
    } else {
      tokenUrl = useB2B ? this.tokenUrlStgB2B : this.tokenUrlStg;
    }

    // Build Authorization header using Okta tokens (no Basic)
    let idToken: string | null = null;
    let accessToken: string | null = null;

    try {
      if (typeof (this.oktaAuth as any).getIdToken === 'function') {
        idToken = (this.oktaAuth as any).getIdToken();
      } else if ((this.oktaAuth as any).tokenManager && typeof (this.oktaAuth as any).tokenManager.get === 'function') {
        const t = (this.oktaAuth as any).tokenManager.get('idToken');
        idToken = t?.idToken || t?.id_token || (t && t.toString && t.toString()) || null;
      }
    } catch (e) {
      idToken = null;
    }

    try {
      if (typeof (this.oktaAuth as any).getAccessToken === 'function') {
        accessToken = (this.oktaAuth as any).getAccessToken();
      } else if ((this.oktaAuth as any).tokenManager && typeof (this.oktaAuth as any).tokenManager.get === 'function') {
        const t = (this.oktaAuth as any).tokenManager.get('accessToken');
        accessToken = t?.accessToken || t?.token || (t && t.toString && t.toString()) || null;
      }
    } catch (e) {
      accessToken = null;
    }

    const authParts: string[] = [];
    if (idToken) authParts.push(`Bearer ${idToken}`);
    if (accessToken) authParts.push(`Bearer ${accessToken}`);

    const headers = new HttpHeaders({
      ...(authParts.length ? { 'Authorization': authParts.join(', ') } : {}),
      'Accept': 'application/json'
    });
    if (this.inflightRequest) {
      return this.inflightRequest;
    }

    const req$ = this.http.post<any>(tokenUrl, null, { headers }).pipe(
      tap((res) => {
        if (res?.access_token) {
          const prefix = (res.access_token || '').substring(0, 12);
          const isJwt = (res.access_token || '').startsWith('eyJ');
        }
      }),
      shareReplay(1),
      finalize(() => {
        this.inflightRequest = null;
      })
    );

    this.inflightRequest = req$;
    return req$;
  }

  // ✅ BehaviorSubject observable (use in interceptor)
  getToken$(): Observable<string | null> {
    return this.tokenSubject.asObservable();
  }

  // ✅ Synchronously get latest token
  getCurrentToken(): string | null {
    return this.tokenSubject.value;
  }

  // ✅ Set new token and store in localStorage
  setToken(token: string, expiresInSeconds: number): void {
    const expiryTimestamp = Date.now() + (expiresInSeconds * 1000); // expiry in ms
    const prefix = token ? token.substring(0, 12) : 'EMPTY';
    const isJwt = token ? token.startsWith('eyJ') : false;
    localStorage.setItem('apigee_token', token);
    localStorage.setItem('apigee_token_expiry', expiryTimestamp.toString());
    this.tokenSubject.next(token);
  }
  isTokenValid(): boolean {
    const expiry = localStorage.getItem('apigee_token_expiry');
    if (!expiry) return false;

    const expiryTime = parseInt(expiry, 10);
    return Date.now() < expiryTime;
  }
}